import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { classNames } from '../lib/format';
import type { StoreSender, StoreSettings } from '../types';

export function SettingsPanel({ advanced }: { advanced: boolean }) {
  const [sender, setSender] = useState<StoreSender>({
    name: '', address: '', city: '', region: '', phone: '', email: ''
  });
  const [settings, setSettings] = useState<StoreSettings>({
    lowStockThreshold: 3,
    newsletterIntro: '',
    ga4Id: '',
    gtmId: '',
    metaPixelId: '',
    tiktokPixelId: '',
    hotjarId: '',
    googleSiteVerification: '',
    whatsappNumber: '',
    instagramUrl: '',
    facebookUrl: ''
  });
  const [features, setFeatures] = useState({ mercadoPagoConfigured: false, starkenConfigured: false, emailConfigured: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.admin.settings().then((res) => {
      setSender(res.sender);
      setSettings(res.settings);
      setFeatures(res.features);
    }).catch(() => undefined);
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.admin.updateSettings({ sender, settings });
      setSender(res.sender);
      setSettings(res.settings);
      setMessage({ tone: 'ok', text: '✓ Configuración guardada.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="dashboard-stack">
      <section className="admin-card wide">
        <div className="admin-card-head">
          <h2>Estado de integraciones</h2>
        </div>
        <ul className="integrations-list">
          <li>
            <span>MercadoPago</span>
            <strong className={classNames('integration-badge', features.mercadoPagoConfigured ? 'ok' : 'pending')}>
              {features.mercadoPagoConfigured ? 'Configurado' : 'Pendiente'}
            </strong>
          </li>
          <li>
            <span>Starken</span>
            <strong className={classNames('integration-badge', features.starkenConfigured ? 'ok' : 'pending')}>
              {features.starkenConfigured ? 'Configurado' : 'Pendiente'}
            </strong>
          </li>
          <li>
            <span>Email transaccional (Resend)</span>
            <strong className={classNames('integration-badge', features.emailConfigured ? 'ok' : 'pending')}>
              {features.emailConfigured ? 'Configurado' : 'Pendiente'}
            </strong>
          </li>
        </ul>
        <p className="muted small">Las integraciones se activan cargando los secretos correspondientes en Cloudflare Pages. El admin se entera al recargar.</p>
      </section>

      <section className="admin-card wide">
        <div className="admin-card-head">
          <h2>Datos del remitente</h2>
          <button type="submit" className="primary-button compact" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
        </div>
        <p className="muted small">Aparecen en la etiqueta de despacho y en los emails al cliente.</p>
        <div className="form-grid" style={{ marginTop: 12 }}>
          <label>
            Nombre tienda
            <input value={sender.name} onChange={(e) => setSender({ ...sender, name: e.target.value })} />
          </label>
          <label>
            Email contacto
            <input type="email" value={sender.email} onChange={(e) => setSender({ ...sender, email: e.target.value })} />
          </label>
          <label className="span-2">
            Dirección
            <input value={sender.address} onChange={(e) => setSender({ ...sender, address: e.target.value })} />
          </label>
          <label>
            Ciudad
            <input value={sender.city} onChange={(e) => setSender({ ...sender, city: e.target.value })} />
          </label>
          <label>
            Región
            <input value={sender.region} onChange={(e) => setSender({ ...sender, region: e.target.value })} />
          </label>
          <label className="span-2">
            Teléfono
            <input value={sender.phone} onChange={(e) => setSender({ ...sender, phone: e.target.value })} />
          </label>
        </div>
      </section>

      <section className="admin-card wide">
        <div className="admin-card-head">
          <h2>Operación</h2>
        </div>
        <div className="form-grid">
          <label>
            Stock bajo (alerta)
            <input type="number" min={0} value={settings.lowStockThreshold} onChange={(e) => setSettings({ ...settings, lowStockThreshold: Number(e.target.value) })} />
          </label>
          <label className="span-2">
            Texto del newsletter
            <textarea rows={2} value={settings.newsletterIntro} onChange={(e) => setSettings({ ...settings, newsletterIntro: e.target.value })} placeholder="Texto que se muestra junto al formulario de suscripción" />
          </label>
        </div>
      </section>

      <section className="admin-card wide">
        <div className="admin-card-head">
          <div>
            <h2>Marketing y SEO</h2>
            <p className="muted small">Pega los IDs / códigos de verificación. Quedan activos al guardar — no necesitas redeploy.</p>
          </div>
        </div>
        <div className="form-grid">
          <label className="span-2">
            Verificación Google Search Console
            <input
              value={settings.googleSiteVerification}
              onChange={(e) => setSettings({ ...settings, googleSiteVerification: e.target.value })}
              placeholder='Solo el contenido del meta (ej: "8a7b6c5d4e...")'
            />
          </label>
          <label>
            Google Analytics 4 (GA4)
            <input
              value={settings.ga4Id}
              onChange={(e) => setSettings({ ...settings, ga4Id: e.target.value })}
              placeholder="G-XXXXXXXXXX"
            />
          </label>
          <label>
            Google Tag Manager
            <input
              value={settings.gtmId}
              onChange={(e) => setSettings({ ...settings, gtmId: e.target.value })}
              placeholder="GTM-XXXXXX"
            />
          </label>
          <label>
            Meta Pixel (Facebook/Instagram)
            <input
              value={settings.metaPixelId}
              onChange={(e) => setSettings({ ...settings, metaPixelId: e.target.value })}
              placeholder="ID de 15-16 dígitos"
            />
          </label>
          <label>
            TikTok Pixel
            <input
              value={settings.tiktokPixelId}
              onChange={(e) => setSettings({ ...settings, tiktokPixelId: e.target.value })}
              placeholder="C0XXXXXXXX..."
            />
          </label>
          <label>
            Hotjar Site ID
            <input
              type="number"
              value={settings.hotjarId}
              onChange={(e) => setSettings({ ...settings, hotjarId: e.target.value })}
              placeholder="3xxxxxx"
            />
          </label>
        </div>
        {advanced && (
          <p className="muted small">
            <strong>Tip Search Console:</strong> al verificar el dominio en Google, elige "etiqueta HTML" → te da algo como&nbsp;
            <code>&lt;meta name="google-site-verification" content="ABC..."/&gt;</code>. Solo pegas el valor (ABC...) acá, no el HTML completo.
          </p>
        )}
      </section>

      <section className="admin-card wide">
        <div className="admin-card-head">
          <h2>Redes sociales y contacto</h2>
        </div>
        <div className="form-grid">
          <label>
            WhatsApp (con código país, sin +)
            <input
              value={settings.whatsappNumber}
              onChange={(e) => setSettings({ ...settings, whatsappNumber: e.target.value })}
              placeholder="56912345678"
            />
          </label>
          <label>
            Instagram URL
            <input
              type="url"
              value={settings.instagramUrl}
              onChange={(e) => setSettings({ ...settings, instagramUrl: e.target.value })}
              placeholder="https://instagram.com/runmorechile"
            />
          </label>
          <label className="span-2">
            Facebook URL
            <input
              type="url"
              value={settings.facebookUrl}
              onChange={(e) => setSettings({ ...settings, facebookUrl: e.target.value })}
              placeholder="https://facebook.com/runmorechile"
            />
          </label>
        </div>
      </section>

      {advanced && (
        <section className="admin-card wide">
          <h2>Variables técnicas (solo lectura)</h2>
          <p className="muted small">
            Variables que viven como secretos en Cloudflare:&nbsp;
            <code>ADMIN_TOKEN</code>, <code>ADMIN_EMAIL</code>, <code>ADMIN_PASSWORD</code>,&nbsp;
            <code>MP_ACCESS_TOKEN</code>, <code>MP_WEBHOOK_SECRET</code>, <code>R2_PUBLIC_BASE_URL</code>,&nbsp;
            <code>FREE_SHIPPING_THRESHOLD</code>, <code>STANDARD_SHIPPING_PRICE</code>,&nbsp;
            <code>STARKEN_CLIENTE_ID</code>, <code>STARKEN_API_TOKEN</code>, <code>STARKEN_API_URL</code>,&nbsp;
            <code>RESEND_API_KEY</code>, <code>STORE_EMAIL_FROM</code>, <code>STORE_EMAIL_ADMIN</code>.
          </p>
        </section>
      )}

      {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
    </form>
  );
}
